import _ from "lodash";
import flatten from "lodash/flatten";
import { ajax } from "lib/observable";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import { CATALOG, SCHEMA } from "constants/action-types";
import siteConfig from "site-config";
import { updateEntities } from "redux-query";
import { normalizeData } from "helpers/data/normalize";
import { stringify } from "querystring";
import CatalogEntries from "schemas/catalogs/catalog-entries";
import CatalogEntry from "schemas/catalogs/catalog-entry";
import { v4 as uuid } from "uuid"; // for sync_id
import { CatalogEntryRecord } from "records/catalog/catalog-entry";
import { arrayMove } from "react-sortable-hoc";
import { activeListFetchEntries, activeListRemove } from "actions/active-list";
import { add as snackbarAdd } from "actions/snackbars";
import { formatMessage } from "lib/globalize-util";
import me from "models/me";
import { ITEM, MAX_PRODUCTS_PER_CALL } from "constants/catalogs";
import { fetch as fetchVariants, getVariantsList } from "actions/variants";
import { IStore } from "reducers";
import { getCatalogErrorMessage } from "helpers/catalog-entries";

export const createDummySchema = (action$, store) =>
    action$.ofType(SCHEMA.DUMMY.CREATE).mergeMap(({ payload, meta }) => {
        const actions = [
            updateEntities({
                schemas: (currentSchemas) => ({
                    ...currentSchemas,
                    ...payload.schema,
                }),
            }),
        ];
        return actions;
    });

const getProductRefs = (productsId) => {
    const lastRevision = 0;

    return productsId.reduce((target, productId: string) => {
        if (productId) {
            target[productId] = lastRevision;
        }
        return target;
    }, {});
};

const getNextProducts = (body) => {
    return ajax({
        body,
        method: "POST",
        url: `${siteConfig.goAPI}/v1/collection_fetch_products_by_ids`,
    })
        .map(({ response }) => response.products)
        .catch((error) => {
            // eslint-disable-next-line no-console
            console.error(error);
            return Observable.of(snackbarAdd({ message: formatMessage("There was an error fething some products.") }));
        });
};

const getActionsAfterFetchCatalogEntries = (data, meta, nextPage) => {
    return [
        updateEntities({
            items: (currentProducts) => ({
                ...currentProducts,
                ...data.entities.items,
            }),
            entries: (currentEntries) => ({
                ...currentEntries,
                ...data.entities.entries,
            }),
        }),
        fetchVariants(getVariantsList(data.entities.items)),
        {
            meta: {
                ...meta,
                replace: !meta.nextPage,
            },
            payload: {
                results: data.result,
                nextPage,
            },
            type: CATALOG.ENTRIES.FETCH.COMPLETED,
        },
    ];
};

export const fetchCatalogEntriesEpic = (action$, store) =>
    action$.ofType(CATALOG.ENTRIES.FETCH.START).switchMap(({ payload, meta }) => {
        const { scope, entities, routing } = store.getState() as IStore;
        const usePost = !meta.fromAssortment;

        const params = {} as any;
        if (usePost) {
            // use POST from /catalog/:id page since GET query can exceed length limit
            params.url = siteConfig.api + `/api/v3.1/catalog/${payload?.id ?? meta.key}/query/entries`;
            params.method = "POST";
            params.body = meta.nextPage ?? payload?.filters;
        } else {
            // /v3.0 to enable filter by deliveries in assortment shop page
            params.url =
                siteConfig.api +
                (meta?.nextPage ?? `/api/v3.0/catalog/${payload.id}/entries?${stringify(payload.filters)}`);
        }

        if (payload?.brand) {
            params.activeBrand = payload.brand;
        }
        if (scope.viewAs) {
            params.company = scope.viewAs._id;
        }

        return ajax(params)
            .flatMap(({ response, xhr }) => {
                const nextPage = xhr.getResponseHeader("Next") || response.nextPagePostBody;
                response = response.data || response;

                if (payload && payload.filters && payload.filters.__search) {
                    const eventParams = {
                        c: "product",
                        a: "search",
                    };
                    const customDimensions = {
                        term: payload.filters.__search,
                        search_origin_page: routing.locationBeforeTransitions.pathname,
                        brand_id: scope.brand._id,
                    };
                    me.nuAnalytics("event", [eventParams, customDimensions]);
                }

                const data = normalizeData(response, CatalogEntries, entities.schemas, scope);
                const { collectionItemMerchantId } = store.getState().collection;
                const productsId = response.reduce((acc, item) => {
                    if (item.type === ITEM) {
                        acc.push(item.item_id);
                    }
                    return acc;
                }, []);

                const bodyToFetchProducts = {
                    merchant_id: collectionItemMerchantId,
                    product_refs: getProductRefs(productsId.splice(0, MAX_PRODUCTS_PER_CALL)),
                    with_snapshot: true,
                };

                if (!meta.fromAssortment) {
                    return getActionsAfterFetchCatalogEntries(data, meta, nextPage);
                }

                return getNextProducts(bodyToFetchProducts)
                    .expand(() => {
                        if (productsId.length > 0) {
                            return getNextProducts({
                                ...bodyToFetchProducts,
                                product_refs: getProductRefs(productsId.splice(0, MAX_PRODUCTS_PER_CALL)),
                            });
                        }
                        return Observable.empty();
                    })
                    .toArray()
                    .mergeMap((response) => {
                        if (data.entities?.items) {
                            const productsWithSnapshot = flatten(response);
                            Object.keys(data.entities.items).forEach((productId: string) => {
                                const currentProductSnapshot = productsWithSnapshot.find(
                                    (productWithSnapshot) => productWithSnapshot.id === productId
                                );

                                data.entities.items[productId].product = currentProductSnapshot;
                            });
                        }

                        return getActionsAfterFetchCatalogEntries(data, meta, nextPage);
                    });
            })
            .takeUntil(action$.ofType(CATALOG.ENTRIES.FETCH.CANCEL))
            .catch((error) => {
                let errors: any[] = [
                    {
                        error: true,
                        meta,
                        payload: error,
                        type: CATALOG.ENTRIES.FETCH.COMPLETED,
                    },
                ];

                if (error.status === 404) {
                    errors = [...errors, snackbarAdd({ message: formatMessage("Catalog unavailable") })];
                }

                return errors;
            });
    });

export const fetchAllEntriesEpic = (action$, store) =>
    action$.ofType(CATALOG.ENTRIES.ALL.FETCH.START).switchMap(({ payload, meta }) => {
        const { scope, entities } = store.getState();

        const endpoint = `/api/v3.1/catalog/${payload.id}/entries/all`;
        const params: any = {
            url: siteConfig.api + endpoint,
        };

        return ajax(params)
            .flatMap(({ response, status, xhr }) => {
                response = response.data || response;
                const data = normalizeData(response, CatalogEntries, entities.schemas, scope);
                const itemIds = Object.values(data.entities.entries || []).map((entry: any) => entry.item_id);

                return [
                    updateEntities({
                        // merge partial entries data to actualize it but not to override
                        entries: (currentEntries) =>
                            _.mergeWith(currentEntries, data.entities.entries, (a, b) =>
                                a && b ? a.merge(b) : a || b
                            ),
                    }),
                    {
                        payload: {
                            result: itemIds, // we need references to the product, not catalog entries
                        },
                        meta: {
                            id: payload.id,
                        },
                        type: CATALOG.ENTRIES.ALL.FETCH.COMPLETED,
                    },
                ];
            })
            .takeUntil(action$.ofType(CATALOG.ENTRIES.ALL.FETCH.CANCEL))
            .catch((error) => {
                let errors: any[] = [
                    {
                        error: true,
                        payload: error,
                        type: CATALOG.ENTRIES.ALL.FETCH.COMPLETED,
                    },
                ];

                if (error.status === 404) {
                    errors = [...errors, snackbarAdd({ message: formatMessage("Catalog unavailable") })];
                }

                return errors;
            });
    });

export const createCatalogEntryEpic = (action$, store) =>
    action$.ofType(CATALOG.ENTRY.ADD.FETCH.START).mergeMap(({ payload, meta }) => {
        const { scope, entities, catalog } = store.getState();
        const currentCatalog = catalog.entities.catalogs[catalog.result];

        return ajax({
            body: {
                item_id: payload.item && payload.item._id,
                type: meta.type,
                sync_id: payload.entry.sync_id,
                [!payload.before || payload.before.length === 24 ? "before" : "before_sync_id"]: payload.before,
                company: scope.viewAs && scope.viewAs._id,
            },
            method: "POST",
            url: `${siteConfig.api}/api/v3.1/catalog/${payload.catalogId}/entries`,
        })
            .flatMap(({ response, status, xhr }) => {
                const data = normalizeData(response, CatalogEntry, entities.schemas, scope);
                const actions = [
                    updateEntities({
                        items: (currentProducts) => ({
                            ...currentProducts,
                            ...data.entities.items,
                        }),
                        entries: (currentEntries) => {
                            delete currentEntries[payload.entry.sync_id];
                            return {
                                ...currentEntries,
                                ...data.entities.entries,
                            };
                        },
                    }),
                    {
                        payload: {
                            syncId: payload.entry.sync_id,
                            results: data.result,
                        },
                        meta: {
                            catalogId: payload.catalogId,
                        },
                        type: CATALOG.ENTRY.ADD.FETCH.COMPLETED,
                    },
                ];

                if (payload.entry.item_id) {
                    // if user adds new product, we need to update the 'cache' of products in
                    // catalog. This is used to display an indicator in the FAB toolbar area.
                    actions.push({
                        type: CATALOG.ENTRIES.ALL.ADD,
                        payload: {
                            catalogId: payload.catalogId,
                            product: payload.entry.item_id,
                        },
                    });
                }

                return actions;
            })
            .catch((error) => {
                const groupedEntries = _.get(error, ["xhr", "response", "rejectedEntriesGroupedByCause"]);

                if (groupedEntries) {
                    return [
                        updateEntities({
                            entries: (currentEntries) => _.omit(currentEntries, payload.entry.id),
                        }),
                        {
                            payload: {
                                rejectedSyncId: payload.entry.sync_id,
                            },
                            meta: {
                                catalogId: payload.catalogId,
                            },
                            type: CATALOG.ENTRY.ADD.FETCH.COMPLETED,
                        },
                        snackbarAdd({ message: getCatalogErrorMessage(currentCatalog, groupedEntries || {}) }),
                    ];
                }

                let errors: any[] = [
                    {
                        error: true,
                        payload: error,
                        meta: {
                            catalogId: payload.catalogId,
                        },
                        type: CATALOG.ENTRY.ADD.FETCH.COMPLETED,
                    },
                ];

                if (error.status === 404) {
                    errors = [
                        ...errors,
                        snackbarAdd({
                            message: formatMessage(
                                "Please wait a moment before adding another product to the linesheet.",
                                {
                                    linesheet:
                                        scope.brand && scope.brand.hasPreference("linesheets_are_catalogs")
                                            ? formatMessage("catalog")
                                            : formatMessage("linesheet"),
                                }
                            ),
                        }),
                    ];
                }

                return errors;
            });
    });

const createEntryForReorder = (item: { _id: string }, type: string): CatalogEntryRecord => {
    const id = uuid();
    const options = { id, type, sync_id: id };
    switch (type) {
        case "item":
            if (!item?._id) {
                return null;
            }
            return new CatalogEntryRecord({
                ...options,
                item_id: item._id,
                item: item._id,
                options: { reorder: true },
            });
        case "separator":
            return new CatalogEntryRecord({ ...options, label: "" });
        case "image":
            return new CatalogEntryRecord({ ...options, options: {} });
        default:
            return null;
    }
};

export const reorderCatalogEntriesEpic = (action$, store) =>
    action$
        .ofType(CATALOG.ENTRIES.REORDER.START)
        .mergeMap(({ payload: { oldIndex, newIndex, index, item, type }, meta }) => {
            const entries = store.getState()?.entries?.collections?.[meta.catalogId]?.results;

            if (oldIndex === undefined) {
                // its an add
                const entryToAdd = createEntryForReorder(item, type);
                if (!entryToAdd) {
                    return []; // skip invalid items
                }
                const results = [...entries];
                results.splice(index, 0, entryToAdd.id);
                return [
                    updateEntities({
                        entries: (currentEntries) => ({ ...currentEntries, [entryToAdd.id]: entryToAdd }),
                    }),
                    {
                        type: CATALOG.ENTRIES.REORDER.COMPLETED,
                        payload: { results },
                        meta,
                    },
                ];
            } else {
                if (oldIndex >= entries.length) {
                    return [];
                }
                return [
                    {
                        type: CATALOG.ENTRIES.REORDER.COMPLETED,
                        payload: { results: arrayMove(entries, oldIndex, _.clamp(newIndex, 0, entries.length - 1)) },
                        meta,
                    },
                ];
            }
        });

export const deleteCatalogEntryEpic = (action$, store) =>
    action$.ofType(CATALOG.ENTRY.DELETE.START).mergeMap(({ payload, meta }) => {
        const { scope, entities } = store.getState();

        return ajax({
            method: "DELETE",
            url: `${siteConfig.api}/api/v3.1/catalog/${meta.catalogId}/entry/${payload.entry.id}`,
        })
            .flatMap(({ response }) => {
                const actions = [
                    activeListRemove(payload.entry.item),
                    updateEntities({
                        entries: (currentEntries) => {
                            delete currentEntries[payload.entry.id];
                            return {
                                ...currentEntries,
                            };
                        },
                    }),
                    {
                        meta: {
                            catalogId: payload.catalogId,
                        },
                        type: CATALOG.ENTRY.DELETE.COMPLETED,
                    },
                    {
                        meta: {
                            catalogId: meta.catalogId,
                        },
                        type: CATALOG.ENTRIES.FILTERS.REFRESH.START,
                    },
                ];

                if (payload.entry.item_id) {
                    // if user adds new product, we need to update the 'cache' of products in
                    // catalog. This is used to display an indicator in the FAB toolbar area.
                    actions.push({
                        type: CATALOG.ENTRIES.ALL.REMOVE,
                        payload: {
                            catalogId: payload.entry.catalog_id,
                            product: payload.entry.item_id,
                        },
                    });
                }

                return actions;
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of({
                        error: true,
                        payload: error,
                        meta: {
                            catalogId: payload.catalogId,
                        },
                        type: CATALOG.ENTRY.DELETE.COMPLETED,
                    })
                )
            );
    });

export const deleteCatalogEntriesFrontendEpic = (action$, store) =>
    action$.ofType(CATALOG.ENTRIES.DELETE.START).flatMap(({ payload, meta }) => {
        const { entities } = store.getState();
        return Observable.of({
            payload,
            meta: {
                entries: entities.entries,
                catalogId: meta.catalogId,
            },
            type: CATALOG.ENTRIES.DELETE.PRUNE,
        });
    });

export const deleteCatalogEntriesBackendEpic = (action$, store) =>
    action$.ofType(CATALOG.ENTRIES.DELETE.PRUNE).mergeMap(({ payload, meta }) => {
        return ajax({
            method: "DELETE",
            url: `${siteConfig.api}/api/v3.1/catalog/${meta.catalogId}/entries?${stringify(payload.query)}`,
        })
            .flatMap(({ response, status, xhr }) => {
                const { activeList } = store.getState();
                const actions = [];
                if (activeList.current && activeList.current.id === meta.catalogId) {
                    actions.push(activeListFetchEntries());
                }
                actions.push({
                    meta,
                    type: CATALOG.ENTRIES.DELETE.COMPLETED,
                });

                actions.push({
                    type: CATALOG.ENTRIES.ALL.FETCH.START,
                    payload: { id: meta.catalogId },
                });

                return actions;
            })
            .catch((error) =>
                Observable.merge(
                    Observable.of({
                        error: true,
                        payload: error,
                        meta,
                        type: CATALOG.ENTRIES.DELETE.COMPLETED,
                    })
                )
            );
    });
